<template>
  <div class="h-screen flex flex-col overflow-hidden bg-gray-100" @keydown.escape="isSidebarOpen = false">
    <div class="w-100 bg-primary-100 flex justify-between items-center text-primary-600 py-0.5 px-4 text-sm" v-if="isAuthenticatedInPinMode">
      <div />
      <div>You are in pin mode as {{ userName }}</div>
      <base-button size="xs" type="secondary" @click="enablePinMode">
        Switch User
        <template #icon>
          <sign-out-icon />
        </template>
      </base-button>
    </div>
    <div class="flex grow overflow-auto">
      <transition leave-active-class="ease-in duration-200">
        <div v-show="isSidebarOpen" class="md:hidden" @click="isSidebarOpen = false">
          <transition enter-from-class="opacity-0" enter-to-class="opacity-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <div @click="isSidebarOpen = false" v-show="isSidebarOpen" class="fixed inset-0 z-30 transition-opacity ease-linear duration-300 backdrop-blur backdrop-filter">
              <div class="absolute inset-0 bg-gray-600 opacity-50"></div>
            </div>
          </transition>
          <div class="fixed inset-0 flex z-40">
            <transition enter-from-class="-translate-x-full" enter-to-class="translate-x-0" leave-from-class="translate-x-0" leave-to-class="-translate-x-full">
              <div v-show="isSidebarOpen" class="flex-1 flex flex-col max-w-xs w-full bg-gray-800 transform ease-in-out duration-300">
                <div class="absolute top-0 right-0 -mr-14 p-1">
                  <base-icon class="mt-2 mr-2" v-show="isSidebarOpen" @click="isSidebarOpen = false">
                    <x-icon class="h-6 w-6 text-white" />
                  </base-icon>
                </div>
                <div class="shrink-0 flex items-center px-4 py-3 text-white font-semibold text-lg nav-bar">
                  <component :is="logo" class="h-10" />
                </div>
                <div class="mt-5 flex-1 h-0 overflow-y-auto">
                  <nav class="pb-6">
                    <router-link
                      v-for="navItem in navItems"
                      :key="navItem.path"
                      :to="navItem.path"
                      @click.prevent="navigateIfNotCurrent(navItem.path)"
                      :class="[
                        'hover:no-underline group flex items-center px-4 py-2 text-sm font-normal mb-1 relative transition-colors duration-100 ease-out',
                        isActivePath(navItem) ? 'bg-gray-900 text-gray-100 hover:text-gray-100' : 'text-gray-400 hover:bg-gray-700 hover:text-gray-400',
                      ]"
                    >
                      <div v-if="isActivePath(navItem)" class="absolute top-0 bottom-0 left-0 w-1 bg-primary rounded-r-md" />
                      <component :is="navItem.icon" :class="['mr-4 h-6 w-6 transition ease-in-out duration-150', isActivePath(navItem) ? 'text-gray-200' : 'text-gray-400']" />
                      {{ navItem.text
                      }}<base-badge
                        v-if="navItem.text === 'Activities' && (isLocationFiltered === true) & (todoCount > 0)"
                        class="ml-2 relative -top-1"
                        :dark="true"
                        color="primary"
                        size="xs"
                      >
                        {{ todoCount }}
                      </base-badge>
                    </router-link>
                  </nav>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </transition>
      <div class="hidden md:flex md:shrink-0 bg-gray-800">
        <div class="flex flex-col w-60">
          <div class="flex items-center h-16 shrink-0 p-4 border-r text-white nav-bar">
            <component :is="logo" class="h-14 py-2" />
          </div>
          <div class="h-0 flex-1 flex flex-col overflow-y-auto">
            <nav class="flex-1 py-4 bg-gray-800 border-r">
              <router-link
                v-for="navItem in navItems"
                :key="navItem.path"
                :to="navItem.path"
                :class="[
                  'hover:no-underline group flex items-center px-4 py-2 text-sm font-normal mb-1 relative transition-colors duration-100 ease-out',
                  isActivePath(navItem) ? 'bg-gray-900 text-gray-100 hover:text-gray-100' : 'text-gray-400 hover:bg-gray-700 hover:text-gray-400',
                ]"
              >
                <div v-if="isActivePath(navItem)" class="absolute top-0 bottom-0 left-0 w-1 bg-primary rounded-r-md" />
                <component :is="navItem.icon" :class="['mr-4 h-6 w-6 transition ease-in-out duration-150', isActivePath(navItem) ? 'text-gray-200' : 'text-gray-400']" />
                {{ navItem.text
                }}<base-badge
                  v-if="navItem.text === 'Activities' && (isLocationFiltered === true) & (todoCount > 0)"
                  class="ml-2 relative -top-1"
                  :dark="true"
                  color="primary"
                  size="xs"
                >
                  {{ todoCount }}
                </base-badge>
              </router-link>
            </nav>
          </div>
        </div>
      </div>
      <div class="flex flex-col w-0 flex-1 overflow-hidden">
        <div :class="['relative shrink-0 flex h-16 bg-white z-20', { 'shadow-sm': $route.meta.showNavShadow }]">
          <button
            @click.stop="isSidebarOpen = true"
            class="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:bg-gray-100 focus:text-gray-600 md:hidden"
            v-if="!isOrganizationUser"
          >
            <menu-icon class="w-6 h-6" />
          </button>
          <div class="flex-1 flex justify-between">
            <div class="flex-1">
              <global-search v-if="isOrganizationBusiness" :business-id="organization.id" class="hidden md:block" />
            </div>
            <div class="flex items-center px-4 sm:px-6 lg:px-8">
              <base-dropdown :options="profileOptions" align="right">
                <div class="flex items-center cursor-pointer">
                  <div class="h-10 w-10">
                    <avatar
                      :business-id="isOrganizationBusiness ? organization.id : null"
                      :user-id="isOrganizationBusiness ? null : userId"
                      :avatar-type="isOrganizationBusiness ? 'business' : 'user'"
                    />
                  </div>
                  <div class="ml-3 leading-4">
                    <div class="text-primary text-sm">
                      {{ cleanUserName }}
                    </div>
                    <div class="text-xs text-gray-500">{{ organization.description }}</div>
                  </div>
                  <chevron-down-icon class="h-5 w-5 ml-2 text-gray-400" />
                </div>
                <template #option="{ option }">
                  <div class="my-1 w-full border-b" v-if="option.isDivider" />
                  <button class="flex items-center px-4 py-2 text-gray-600 focus:outline-none" @click="option.onClick()" v-else>
                    <component :is="option.icon" class="h-5 w-5 mr-2.5 shrink-0" />
                    <div class="text-left">
                      <div :class="[{ 'text-sm leading-4': option.description }]">{{ option.title }}</div>
                      <div class="text-xs text-gray-500" v-if="option.description">{{ option.description }}</div>
                    </div>
                  </button>
                </template>
              </base-dropdown>
            </div>
          </div>
        </div>
        <div v-if="showActivationHeader" class="flex gap-2 justify-between border-t border-b bg-red-50 text-red-600 py-6 px-4 sm:px-6 lg:px-8 text-base">
          <div class="flex-col">
            <div>Not Active</div>
            <div v-if="numRemainingActivationSteps > 3" class="text-xs">
              Let's get started! Complete {{ numRemainingActivationSteps }} more steps to activate your business and get your {{ organization.customerLabelPlural }} signing up.
            </div>
            <div v-else class="text-xs">
              You're almost there! Only {{ numRemainingActivationSteps }} more steps to activate your business and get your {{ organization.customerLabelPlural }} signing up.
            </div>
          </div>
          <base-button class="my-auto flex" type="primary" size="sm" @click="activation">
            <template v-slot:icon>
              <task-list-icon />
            </template>
            Checklist
          </base-button>
        </div>
        <main :class="['flex-1 relative overflow-y-auto focus:outline-none', { 'pb-12': isOrganizationUser }]" tabindex="0">
          <slot />
        </main>
        <nav class="absolute inset-x-0 bottom-0 flex md:hidden bg-white shadow-xl" v-if="isOrganizationUser">
          <router-link
            v-for="navItem in navItems"
            :key="navItem.path"
            :to="navItem.path"
            :class="[
              'hover:no-underline px-0 py-2 text-sm font-normal relative transition-colors duration-100 ease-out flex-1 flex flex-col items-center',
              isActivePath(navItem) ? 'text-primary hover:text-primary' : 'text-gray-500 hover:text-gray-600',
            ]"
          >
            <div v-if="isActivePath(navItem)" class="absolute bottom-0 w-20 h-1 bg-primary rounded-t-md" />
            <component :is="navItem.icon" :class="['h-6 w-6 transition ease-in-out duration-150 mx-auto', isActivePath(navItem) ? 'text-primary' : 'text-gray-500']" />
            {{ navItem.shortText || navItem.text }}
          </router-link>
        </nav>
      </div>
    </div>
    <notification-wrapper />
    <global-error />
  </div>
</template>

<script>
import Avatar from "@/components/Avatar.vue";
import NotificationWrapper from "@/components/v2/NotificationWrapper";
import GlobalError from "@/components/v2/GlobalError";
import GlobalSearch from "@/components/v2/GlobalSearch";
import PredictileLogo from "@/assets/images/current_color_logo_horizontal.svg";
import RxHarmonyLogo from "@/assets/images/rx-harmony-logo.svg";
import PredictileColorLogo from "@/assets/images/predictile_logo_rgb.svg";
import {
  AlertIcon,
  MailIcon,
  MenuIcon,
  XIcon,
  ChevronDownIcon,
  UserCircleIcon,
  UserIcon,
  DocumentIcon,
  SignOutIcon,
  BusinessIcon,
  UsersIcon,
  CombineIcon,
  TestTubeIcon,
  GridIcon,
  SupportIcon,
  GraphIcon,
  PillIcon,
  CheckIcon,
  TrainingIcon,
  TableIcon,
  ReportIcon,
  SettingsIcon,
  LocationIcon,
  KeyIcon,
  TaskListIcon,
} from "@/assets/svgs";
import { mapGetters, mapActions, mapState } from "vuex";
import redirect from "@/mixins/redirect";
import { capitalize } from "lodash";

export default {
  mixins: [redirect],
  components: {
    Avatar,
    AlertIcon,
    MailIcon,
    NotificationWrapper,
    GlobalError,
    GlobalSearch,
    PredictileLogo,
    RxHarmonyLogo,
    PredictileColorLogo,
    MenuIcon,
    XIcon,
    ChevronDownIcon,
    UserCircleIcon,
    UserIcon,
    DocumentIcon,
    SignOutIcon,
    BusinessIcon,
    UsersIcon,
    CombineIcon,
    TestTubeIcon,
    GridIcon,
    SupportIcon,
    GraphIcon,
    PillIcon,
    CheckIcon,
    TrainingIcon,
    TableIcon,
    ReportIcon,
    SettingsIcon,
    LocationIcon,
    KeyIcon,
    TaskListIcon,
  },
  data() {
    return {
      isSidebarOpen: false,
      todoCount: 0,
      isLoadingCount: false,
      businessHasPins: false,
      todoInterval: null,
    };
  },
  computed: {
    ...mapGetters("cognito", { userId: "id", userName: "name", userEmail: "email" }),
    ...mapGetters("organization", [
      "organization",
      "organizations",
      "isOrganizationPredictile",
      "isOrganizationBusiness",
      "isOrganizationUser",
      "hasMultipleOrganizations",
      "cobrandId",
    ]),
    ...mapGetters("role", ["isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isLocationFiltered", "isPscSecondaryRole", "can", "isPermissionGoverned"]),
    ...mapGetters("location", ["location", "locations"]),
    ...mapGetters("businessActivation", [
      "hasBusinessProfile",
      "hasProductsAndPlans",
      "hasBrandColorsAndLogo",
      "hasTaxRates",
      "hasLocation",
      "hasSubscription",
      "hasMerchantProcessing",
    ]),
    ...mapState("cognito", ["isAuthenticatedInPinMode"]),
    cleanUserName() {
      if (this.isOrganizationUser) return this.organization.customerLabelSingular !== null ? capitalize(this.organization.customerLabelSingular) + " Profile" : "Member Profile";
      else return this.userName.replace("Branch Development: ", "");
    },
    showActivationHeader() {
      return this.isOrganizationBusiness && !this.organization.isActive;
    },
    numRemainingActivationSteps() {
      let numRemaining = 0;
      if (!this.hasBusinessProfile) numRemaining++;
      if (!this.hasProductsAndPlans) numRemaining++;
      if (!this.hasBrandColorsAndLogo) numRemaining++;
      if (!this.hasTaxRates) numRemaining++;
      if (!this.hasLocation) numRemaining++;
      if (!this.hasSubscription) numRemaining++;
      if (!this.hasMerchantProcessing) numRemaining++;
      return numRemaining;
    },
    profileOptions() {
      const options = [];
      if (this.hasMultipleOrganizations || this.isPredictile) {
        options.push(
          ...this.organizations
            .filter((org) => org.id !== this.organization.id)
            .map((org) => ({
              title: org.description,
              description: this.getOrganizationDescriptor(org),
              icon: this.getOrganizationIcon(org),
              onClick: () => this.updateOrganization(org.id),
            })),
        );
        options.push({ isDivider: true });
      }
      if (this.canEnablePinMode && this.can("dashboard.pin-mode")) {
        options.push(
          {
            title: "Enable Pin Mode",
            icon: "key-icon",
            onClick: this.enablePinMode,
          },
          {
            isDivider: true,
          },
        );
      }
      options.push(
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        {
          title: "My Profile",
          icon: "user-icon",
          onClick: () => {
            const targetRoute = `/users/${this.userId}/profile/`;
            if (this.$route.path !== targetRoute) {
              this.$router.push(targetRoute);
            }
          },
        },
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        { title: "Sign Out", icon: "sign-out-icon", onClick: () => this.$router.push(`/signout${this.cobrandId ? `?cobrand=${this.cobrandId}` : ""}`) },
      );

      return options;
    },
    locationOptions() {
      return [...this.locations, { name: "All locations", isAll: true }];
    },
    navItems() {
      if (this.isOrganizationPredictile) {
        return this.predictileLinks;
      } else if (this.isOrganizationBusiness) {
        return this.businessLinks;
      } else if (this.isOrganizationUser) {
        return this.userLinks;
      }

      return [];
    },
    predictileLinks() {
      return [
        { text: "Businesses", icon: "business-icon", path: "/businesses" },
        { text: "Users", icon: "users-icon", path: "/admin/users" },
        { text: "Integrations", icon: "combine-icon", path: "/admin/integrations" },
        { text: "Services", icon: "test-tube-icon", path: "/admin/services" },
        { text: "Email Templates", icon: "mail-icon", path: "/admin/email" },
        { text: "Reconcile", icon: "report-icon", path: "/admin/subscription-reconcile" },
      ];
    },
    businessLinks() {
      const isBusinessManagement = this.isPredictile() || this.isBusinessManagement(this.organization.id);
      const isBusinessAdministrator = this.isPredictile() || this.isBusinessAdministrator(this.organization.id);

      const matrixGoverned = this.isPermissionGoverned;
      const links = [
        { text: "Dashboard", icon: "graph-icon", path: `/businesses/${this.organization.id}/home`, cap: "home" },
        {
          text: capitalize(this.organization.customerLabelPlural),
          icon: "users-icon",
          path: `/businesses/${this.organization.id}/members`,
          cap: "members",
        },
        ...(this.organization.usePrescriptionProgramSetting === true
          ? [{ text: "Prescriptions", icon: "pill-icon", path: `/businesses/${this.organization.id}/prescriptions`, cap: "prescriptions" }]
          : []),
        ...(this.organization.useTodoSetting === true && this.isLocationFiltered === false
          ? [
              {
                text: "Activities",
                icon: "check-icon",
                path: `/businesses/${this.organization.id}/business-todo/unassigned`,
                cap: "business-todo",
              },
            ]
          : []),
        ...(matrixGoverned || (isBusinessAdministrator === true && this.isLocationFiltered === false)
          ? [
              {
                text: "Disputes",
                icon: "alert-icon",
                path: `/businesses/${this.organization.id}/disputes`,
                cap: "disputes",
              },
            ]
          : []),
        ...(this.organization.useTodoSetting === true && this.isLocationFiltered === true
          ? [
              {
                text: "Activities",
                icon: "check-icon",
                path: `/businesses/${this.organization.id}/location-todo/${this.cobrandId === "rx-harmony" ? "follow-up" : "due-today"}`,
                cap: "business-todo",
              },
            ]
          : []),
        { text: "Training", icon: "training-icon", path: `/businesses/${this.organization.id}/trainings/mytraining`, cap: "trainings" },
        ...(matrixGoverned || !(!isBusinessManagement || this.isPscSecondaryRole || (this.isLocationFiltered && this.organization.operatingModel === "coordinator"))
          ? [{ text: "Plans & Products", icon: "table-icon", path: `/businesses/${this.organization.id}/offerings`, cap: "offerings" }]
          : []),
        ...(matrixGoverned || !this.isPscSecondaryRole ? [{ text: "Reports", icon: "report-icon", path: `/businesses/${this.organization.id}/reports`, cap: "reports" }] : []),
        { text: "Settings", icon: "settings-icon", path: `/businesses/${this.organization.id}/business`, cap: "business" },
        ...(matrixGoverned || !this.isPscSecondaryRole ? [{ text: "Support", icon: "support-icon", path: `/businesses/${this.organization.id}/support`, cap: "support" }] : []),
      ];
      return links.filter((link) => !link.cap || this.can(link.cap));
    },
    userLinks() {
      return [
        { text: "My Plans", shortText: "Plans", icon: "grid-icon", path: `/users/${this.organization.id}/plans` },
        { text: "My Documents", shortText: "Documents", icon: "document-icon", path: `/users/${this.organization.id}/documents` },
        { text: "My Profile", shortText: "Profile", icon: "user-icon", path: `/users/${this.organization.id}/profile` },
        { text: "Support", icon: "support-icon", path: "/user/support" },
      ];
    },
    logo() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return "rx-harmony-logo";
      }
      return "predictile-logo";
    },
    canEnablePinMode() {
      return this.isOrganizationBusiness && this.businessHasPins;
    },
  },
  created() {
    this.loadTodoCount();

    setInterval(
      function () {
        this.loadTodoCount();
      }.bind(this),
      90000,
    );
    this.checkForBusinessPins();
    this.EventBus.$on("business-user-updated", this.checkForBusinessPins);
  },
  beforeUnmount() {
    this.EventBus.$off("business-user-updated", this.checkForBusinessPins);

    if (this.todoInterval) {
      clearInterval(this.todoInterval);
      this.todoInterval = null;
    }
  },

  watch: {
    // userId(newVal) {
    //   if (newVal) {
    //     this.loadTodoCount();
    //     this.startTodoInterval();
    //   }
    // },
    organization() {
      this.checkForBusinessPins();
    },
  },
  methods: {
    ...mapActions("organization", ["changeOrganization"]),
    ...mapActions("location", ["updateLocation"]),
    isActivePath(navItem) {
      if (navItem.text === "Activities" && this.isLocationFiltered === false)
        return this.$route.path.toLowerCase().startsWith("/businesses/" + this.organization.id + "/business-todo");
      else if (navItem.text === "Settings")
        return (
          this.$route.path.toLowerCase().startsWith(`/businesses/${this.organization.id}/business`) &&
          !this.$route.path.toLowerCase().startsWith(`/businesses/${this.organization.id}/business-todo`)
        );
      else return this.$route.path.toLowerCase().startsWith(navItem.path.toLowerCase());
    },
    navigateIfNotCurrent(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    getOrganizationIcon(org) {
      switch (org.type) {
        case "predictile":
          return "predictile-color-logo";
        case "business":
          return "business-icon";
        case "user":
          return "user-circle-icon";
      }
    },
    getOrganizationDescriptor(org) {
      switch (org.type) {
        case "predictile":
          return "Predictile";
        case "business":
          return "Business User";
        case "user":
          return this.organization.customerLabelSingular !== null ? capitalize(this.organization.customerLabelSingular) + " Profile" : "Member Profile";
      }
    },
    async updateOrganization(id) {
      await this.changeOrganization(id);
      await this.redirectToHome();
    },
    startTodoInterval() {
      if (this.todoInterval) return;

      this.todoInterval = setInterval(() => {
        this.loadTodoCount();
      }, 90000);
    },
    loadTodoCount() {
      if (!this.userId) return;

      if (this.isLoadingCount === true || !this.organization || this.organization.useTodoSetting === false || this.isLocationFiltered === false) return;

      this.isLoadingCount = true;
      this.$axios
        .get(`/businesses/${this.organization.id}/todo/location-user-assigned/count`)
        .then((res) => {
          this.todoCount = res.data.count;
        })
        .catch((err) => {
          if (err && err.response && err.response.status === 401) {
            console.warn("Skipping todo count — unauthorized");
          } else {
            console.error("Todo count failed", err);
          }
        })
        .finally(() => {
          this.isLoadingCount = false;
        });
    },
    activation() {
      this.$router.push(`/activation/${this.organization.id}`);
    },
    async checkForBusinessPins() {
      try {
        if (!this.isOrganizationBusiness) return;
        const response = await this.$axios.get(`/business/${this.organization.id}/has-pins`);
        this.businessHasPins = response.data.hasPins;
      } catch {
        this.businessHasPins = false;
      }
    },
    async enablePinMode() {
      sessionStorage.removeItem("pinMode");
      sessionStorage.setItem("hasStartedPinMode", true);
      await this.$axios.post(`/businesses/${this.organization.id}/enable-pin-mode`);
      this.$router.push("/pin-mode");
    },
  },
};
</script>

<style lang="scss">
.nav-bar {
  background-color: var(--navbar-bg) !important;
}
</style>
